import { useMemo } from 'react';
import DataTable from '../../../../../../../../../components/Common/UI/DataTable';
import Icons from '../../../../../../../../../components/Common/Icons/Icons';
import ContentCard from '../../../../../../../../../Guidance/components/Common/Card';
import CheckBox from '../../../../../../../../../Guidance/components/Common/UI/CheckBox';
import {
  ISearchResultData,
  searchResultCol,
} from '../../../../../../../../../Guidance/components/Pages/BatchDownload/Data';
import { Button } from '../../../../../../../../../components/Common/UI/Button';
import BatchProcessingNotification from '../../../../../../../../../Guidance/components/Pages/BatchDownload/BatchZip';
import createBatchDownloadBlob from '../../../../../../../../../Guidance/components/Pages/BatchDownload/utils';

interface IBatchDownloadTableProps {
  searchResult: ISearchResultData[];
  handleCompressIntoPDF: () => void;
  selectAllRows: boolean;
  handleSelectAllRows: (payload: boolean) => void;
  handleSearchResult: (payload: ISearchResultData[]) => void;
  searchQuery: string;
  onScrollTop?: () => void;
}
type RowWithAccountId = ISearchResultData & { accountId?: string };

function BatchDownloadTable({
  searchResult,
  handleCompressIntoPDF,
  selectAllRows,
  handleSelectAllRows,
  handleSearchResult,
  searchQuery,
  onScrollTop,
}: IBatchDownloadTableProps) {
  // const selectedRows = useMemo(() => (
  //   Object.fromEntries(searchResult.map(() => selectAllRows).entries())
  // ), [selectAllRows]);

  const selectedPages = useMemo(() => {
    const ids = new Set((searchResult as RowWithAccountId[]).filter(r => r.checked).map(r => r.accountId ?? r.account));

    return ids.size;
  }, [searchResult]);

  const totalPages = useMemo(() => {
    const ids = new Set((searchResult as RowWithAccountId[]).map(r => r.accountId ?? r.account));

    return ids.size;
  }, [searchResult]);

  const selectedRows = useMemo(
    () => Object.fromEntries(searchResult.map((row, idx) => [idx, !!row.checked])),
    [searchResult],
  );

  const handleCompress = async () => {
    handleCompressIntoPDF();
    const selectedCount = Math.min(searchResult.filter(el => el.checked).length, 50);

    return createBatchDownloadBlob(selectedCount);
  };

  return (
    <div>
      {searchResult.length ? (
        <ContentCard
          title="Search Results"
          subTitle="Please select which statements you want to combine
          into a ZIP by clicking on the check box under the “SELECT ALL” column.
           After you’ve select all the statements, click on the Combine into Single PDF
           or Compress into ZIP button to generate the job."
          titleStyle="mb-[10px]"
          className="mt-[20px] border rounded-none shadow-none"
        >
          <div
            className="sm:pl-[20px] flex my-[20px] sm:items-center sm:gap-[20px] gap-[15px] xs:flex-row flex-col
          flex-wrap"
          >
            <CheckBox
              id="selectAllRows"
              label="Select All"
              checked={selectAllRows}
              onClick={() => {
                handleSelectAllRows(!selectAllRows);
                handleSearchResult(searchResult.map(el => ({ ...el, checked: !selectAllRows })));
              }}
            />
            <div className="flex items-center gap-[5px] text-[12px]">
              <div className="text-addition1 min-w-5">{`${selectedPages}/${totalPages}`}</div>
              <div className="text-addition1-50">Pages Selected</div>
            </div>

            <div className="flex items-center gap-[5px] text-[12px]">
              <div className="text-addition1 min-w-5">{`${searchResult.filter(el => el.checked).length}/${searchResult.length}`}</div>
              <div className="text-addition1-50">Documents Selected</div>
            </div>
          </div>
          <DataTable
            responsive
            columns={searchResultCol}
            data={searchResult}
            paginationPosition="bottom"
            selectedRows={selectedRows}
            initialState={{ pagination: { pageSize: 10 } }}
            onRow={row => {
              const isNext = !row.getIsSelected();

              row.toggleSelected(isNext);

              const rowId = (row.original as ISearchResultData).id;

              handleSearchResult(searchResult.map(el => (el.id === rowId ? { ...el, checked: isNext } : el)));
            }}
          />
          <div className="flex gap-[10px] flex-wrap sm:justify-center items-center mt-[20px]">
            <Button
              className="px-[20px] py-[3px] xs:h-[32px] h-[38px] !bg-[#522E5E] !text-white hover:opacity-80
            xs:w-auto w-full"
            >
              <Icons.PDF2 className="[&_path]:fill-white h-[18px]" />
              Combine Into Single PDF
            </Button>
            <BatchProcessingNotification
              onScrollTop={onScrollTop}
              onCompress={handleCompress}
              disabled={searchResult.filter(el => el.checked).length === 0}
              className="px-[20px] py-[3px] xs:h-[32px] h-[38px] !bg-primary !text-white hover:opacity-80
              xs:w-auto w-full"
            />
          </div>
        </ContentCard>
      ) : null}
      {searchQuery.length && searchQuery.length < 3 ? (
        <div className="text-[#F25B2A] font-bold text-center mt-[20px]">Select at least 3 characters for search</div>
      ) : null}
    </div>
  );
}

export default BatchDownloadTable;
